import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Send, Loader2, Instagram } from "lucide-react";
import { COMPANY, INSTAGRAM } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INITIAL = { name: "", phone: "", email: "", message: "" };

const inputClass =
  "w-full border border-white/10 bg-white/5 px-4 py-3.5 text-sm text-white placeholder:text-neutral-600 outline-none transition-colors duration-200 focus:border-white/50";

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Tack! Din förfrågan är skickad. Vi återkommer så snart vi kan.");
      setForm(INITIAL);
    } catch (err) {
      toast.error("Något gick fel. Försök igen eller ring mig direkt på " + COMPANY.phone + ".");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="kontakt" data-testid="contact-section" className="py-20 sm:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mb-14 max-w-3xl sm:mb-16"
        >
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-neutral-500">07 / Kontakt</p>
          <h2 className="mt-4 font-display text-4xl font-bold uppercase leading-[0.98] tracking-tight text-white sm:text-6xl">
            Har du ett projekt på gång?
          </h2>
          <p className="mt-5 text-base leading-relaxed text-neutral-300 sm:text-lg">
            Behöver du hjälp med en fjärrvärmecentral, VVS, rör eller svetsning? Hör av dig så går vi igenom vad du
            behöver hjälp med och hittar en bra lösning.
          </p>
        </motion.div>

        <div className="grid gap-10 lg:grid-cols-12">
          <motion.form
            data-testid="contact-form"
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            onSubmit={submit}
            className="steel-card hairline-t border border-white/10 p-6 sm:p-10 lg:col-span-7"
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="contact-name" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                  Namn *
                </label>
                <input
                  id="contact-name"
                  data-testid="contact-input-name"
                  type="text"
                  required
                  minLength={2}
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Ditt namn"
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="contact-phone" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                  Telefon *
                </label>
                <input
                  id="contact-phone"
                  data-testid="contact-input-phone"
                  type="tel"
                  required
                  minLength={5}
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="070-123 45 67"
                  className={inputClass}
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="contact-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                  E-post *
                </label>
                <input
                  id="contact-email"
                  data-testid="contact-input-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={update("email")}
                  placeholder="din@epost.se"
                  className={inputClass}
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="contact-message" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                  Vad behöver du hjälp med? *
                </label>
                <textarea
                  id="contact-message"
                  data-testid="contact-input-message"
                  required
                  minLength={5}
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Beskriv kort vad du behöver hjälp med..."
                  className={`${inputClass} resize-y`}
                />
              </div>
            </div>
            <button
              type="submit"
              data-testid="contact-submit-button"
              disabled={sending}
              className="group mt-8 flex w-full items-center justify-center gap-2 bg-white px-7 py-4 font-mono text-xs uppercase tracking-[0.18em] text-black transition-colors duration-200 hover:bg-neutral-300 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              {sending ? (
                <>
                  Skickar...
                  <Loader2 className="h-4 w-4 animate-spin" />
                </>
              ) : (
                <>
                  Skicka förfrågan
                  <Send className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                </>
              )}
            </button>
          </motion.form>

          <motion.aside
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ delay: 0.12, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col justify-between gap-10 border border-white/10 bg-coal p-6 sm:p-10 lg:col-span-5"
            data-testid="contact-info-panel"
          >
            <div>
              <img src="/assets/logo-white.webp" alt="Uusimaa Svets & VVS AB logotyp" className="h-11 w-auto" loading="lazy" />
              <p className="mt-6 border-l-2 border-weld pl-4 font-mono text-xs uppercase tracking-[0.18em] text-neutral-400">
                ”{COMPANY.motto}”
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-weld" />
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Adress</p>
                  <p className="mt-1 text-sm text-neutral-200">
                    {COMPANY.name}
                    <br />
                    {COMPANY.address1}
                    <br />
                    {COMPANY.address2}
                  </p>
                  <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-500">
                    Org.nr {COMPANY.orgNr}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Phone className="mt-0.5 h-4 w-4 shrink-0 text-weld" />
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Telefon</p>
                  <a
                    data-testid="contact-phone-link"
                    href={COMPANY.phoneHref}
                    className="mt-1 block text-sm text-neutral-200 underline-offset-4 transition-colors duration-200 hover:text-white hover:underline"
                  >
                    {COMPANY.phone}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Mail className="mt-0.5 h-4 w-4 shrink-0 text-weld" />
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">E-post</p>
                  <a
                    data-testid="contact-email-link"
                    href={COMPANY.emailHref}
                    className="mt-1 block break-all text-sm text-neutral-200 underline-offset-4 transition-colors duration-200 hover:text-white hover:underline"
                  >
                    {COMPANY.email}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Instagram className="mt-0.5 h-4 w-4 shrink-0 text-weld" />
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Instagram</p>
                  <a
                    data-testid="contact-instagram-link"
                    href={INSTAGRAM.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 block text-sm text-neutral-200 underline-offset-4 transition-colors duration-200 hover:text-white hover:underline"
                  >
                    {INSTAGRAM.handle}
                  </a>
                </div>
              </div>
            </div>

            <p className="border-t border-white/10 pt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
              Verksam i hela {COMPANY.area}
            </p>
          </motion.aside>
        </div>
      </div>
    </section>
  );
}
